/**
 * Folder-mode plumbing for a CMS Scene (CONTENT_MODEL_SPEC §2).
 *
 * A scene is a **folder**: one glTF 2.1 document plus the files it names, and
 * the document names them with relative URIs (`assets/<sha256>.<ext>`) that a
 * reader resolves against the document's own URL. Saving is therefore two
 * calls, not an archive rewrite: the folder's files go to
 * `POST /scenes/{id}/files` (content-addressed, so re-sending bytes the folder
 * already holds costs one 200), and the document goes to `POST /contents`.
 * Scene and asset saves both go through {@link saveFolder}.
 *
 * Everything here takes its I/O as parameters, so it can be tested without
 * a network.
 */

import { isFolderFileUri, uploadFolder } from 'trace-viewer/editor';
import type { ManifestValidationOptions, SceneFolder } from 'trace-viewer/editor';
import type { SceneObjectData } from 'trace-viewer';
import type { ResourceManager } from 'trace-viewer/scene';

// ── Folder addressing ───────────────────────────────────────────────

/** The container every content folder lives in (spec §2.1). */
export const TRACE_CONTENT_CONTAINER = 'trace-content';

/**
 * Absolute URL of a scene's content folder, ending in `/`.
 *
 * Preferred source is the current `RootContent` row's own URL: the document
 * lives *in* the folder, so its directory is the folder, whatever host or
 * path prefix the row was written with (and whatever a future rehoming does).
 * The `{blobBase}/trace-content/scenes/{id}/` form is the fallback for a scene
 * that has no document yet — a new scene taking its first import before its
 * first save.
 */
export function sceneFolderBaseUrl(
    blobBaseUrl: string,
    sceneId: string,
    rootContentUrl?: string | null,
): string {
    if (rootContentUrl) {
        const dir = directoryUrl(rootContentUrl);
        if (dir) return dir;
    }
    return `${blobBaseUrl.replace(/\/$/, '')}/${TRACE_CONTENT_CONTAINER}/scenes/${sceneId}/`;
}

/** The directory an absolute URL sits in, ending in `/`. Null if unparseable. */
function directoryUrl(url: string): string | null {
    try {
        const parsed = new URL(url);
        parsed.hash = '';
        parsed.search = '';
        const slash = parsed.pathname.lastIndexOf('/');
        if (slash < 0) return null;
        parsed.pathname = parsed.pathname.slice(0, slash + 1);
        return parsed.toString();
    } catch {
        return null;
    }
}

/**
 * Strip the authoring `./` a blob import puts on a src.
 *
 * `TraceScene` writes `./assets/<name>` for an imported blob while a document
 * authored by the writer says `assets/<name>`. They name the same folder file;
 * `ResourceManager` normalizes the same way.
 */
export function normalizeFolderUri(src: string): string {
    return src.startsWith('./') ? src.slice(2) : src;
}

/** Whether `src` addresses a file of the scene's own folder (spec §2.1). */
export function isFolderFileSrc(src: string): boolean {
    return isFolderFileUri(normalizeFolderUri(src));
}

// ── The manifest pre-flight (spec §2.5) ─────────────────────────────

/**
 * The pre-flight a **scene** document is exported under.
 *
 * Both bases are supplied on purpose, and each closes a hole if it is not:
 * without `apiBaseUrl` there is no library-reference class at all and every
 * live reference is refused (fail-closed, the posture the server takes);
 * without `storageBaseUrl` a URL naming our blob storage directly would pass,
 * and such a URL aliases bytes the referencing document does not own and is
 * invisible to the per-folder reference set the sweeper is built on.
 */
export function sceneManifestOptions(apiBaseUrl: string, storageBaseUrl: string): ManifestValidationOptions {
    return { apiBaseUrl, storageBaseUrl };
}

/**
 * The pre-flight an **asset** document is exported under: the scene options
 * plus the closure rule (spec §3.2).
 *
 * An asset folder is closed over its own files, may reference library assets
 * and streamable media, and never references a scene folder — a scene's file
 * is owned by that scene and dies with it, so an asset pointing into one would
 * be a dangling reference the day that scene is deleted.
 */
export function assetManifestOptions(apiBaseUrl: string, storageBaseUrl: string): ManifestValidationOptions {
    return { ...sceneManifestOptions(apiBaseUrl, storageBaseUrl), rejectSceneFolderUrls: true };
}

/**
 * The classification options a **promotion** passes (spec §3.5).
 *
 * The same options an asset export runs under, minus the closure rule — which
 * is not a caller's to choose: `promoteToAsset` always rejects scene-folder
 * URLs, because refusing them is the whole reason promotion is its own verb.
 * Derived from {@link assetManifestOptions} rather than restated, so the two
 * cannot drift into disagreeing about what an asset may reference.
 */
export function promotionManifestOptions(
    apiBaseUrl: string,
    storageBaseUrl: string,
): Omit<ManifestValidationOptions, 'rejectSceneFolderUrls'> {
    const { rejectSceneFolderUrls: _closure, ...rest } = assetManifestOptions(apiBaseUrl, storageBaseUrl);
    return rest;
}

// ── Reading the folder files a scene names ──────────────────────────

/**
 * Every folder-relative reference the scene's export data carries.
 *
 * Discovery is structural — every string anywhere in the data that has the
 * shape of a folder file — for the same reason `tracex-exporter` walks
 * behaviors structurally rather than through a field allowlist: a behavior
 * that grows a new asset field must not silently stop being seen.
 */
export function collectFolderFileSrcs(sceneData: readonly SceneObjectData[]): string[] {
    const found = new Set<string>();
    const visited = new WeakSet<object>();

    const walk = (value: unknown, depth: number): void => {
        if (depth > 12 || value === null || typeof value !== 'object') return;
        if (visited.has(value)) return;
        visited.add(value);
        for (const entry of Object.values(value as Record<string, unknown>)) {
            if (typeof entry === 'string') {
                if (isFolderFileSrc(entry)) found.add(normalizeFolderUri(entry));
            } else if (entry !== null && typeof entry === 'object') {
                walk(entry, depth + 1);
            }
        }
    };

    for (const data of sceneData) walk(data, 0);
    return [...found];
}

// ── Saving a folder ─────────────────────────────────────────────────

/**
 * What `POST /scenes/{id}/files` needs from a client, and nothing more.
 *
 * Named for where it was first needed, but the contract is folder-neutral:
 * *put these bytes in the folder owned by this id, and answer the relative
 * uri they are stored under*. `sceneId` is therefore **the folder owner's
 * id** — an asset id when the implementation is `assetFileUploader`
 * (`asset-folder.ts`), whose endpoint is the asset twin `POST
 * /assets/{id}/files` (spec §2.4).
 */
export interface SceneFileUploader {
    uploadSceneFile(sceneId: string, file: Blob, fileName: string): Promise<{ uri: string }>;
}

/** What a save needs of the scene's record of files it stored (trace-viewer `ResourceManager`). */
export type StoredFileRecords = Pick<ResourceManager, 'recordStoredFiles' | 'forgetStoredFiles' | 'hasStoredFiles'>;

/** One folder save: where it goes, how to export, upload and write. */
export interface FolderSave<T> {
    /** The destination folder's URL, ending in `/`; also the records' key. */
    folderUrl: string;
    resources: StoredFileRecords;
    /** Export the folder into `folderUrl`; called again on a retry. */
    exportFolder(): Promise<SceneFolder>;
    /** The folder's owner and its `POST …/files`. */
    ownerId: string;
    uploader: SceneFileUploader;
    /** Write the document (`POST /contents`, `PATCH /assets/{id}`). */
    write(document: Blob): Promise<T>;
}

/**
 * Save a folder (spec §2.2): export, upload the files through trace-viewer's
 * `uploadFolder` (sheet dependencies first, the document repointed at the
 * stored names, each stored file recorded), then write the document.
 *
 * The export names what `folderUrl` already holds on `existing` — files
 * streamed from it, imports registered at it, and files this session stored
 * there with unchanged bytes — so a second save of unchanged files writes the
 * document alone.
 *
 * A record goes stale only if the folder loses the file, and the server then
 * refuses the document (400). When the save started with records for the
 * folder, that write is retried once with them forgotten, which re-uploads
 * every file the session has bytes for. A refused file upload is not retried.
 */
export async function saveFolder<T>(save: FolderSave<T>): Promise<{ folder: SceneFolder; document: Blob; written: T }> {
    const hadRecords = save.resources.hasStoredFiles(save.folderUrl);
    const upload = (uri: string, blob: Blob) =>
        save.uploader.uploadSceneFile(save.ownerId, blob, uri.slice(uri.lastIndexOf('/') + 1));
    const attempt = async () => {
        const folder = await save.exportFolder();
        const document = await uploadFolder(folder, save.folderUrl, upload, save.resources);
        return { folder, document };
    };

    const first = await attempt();
    try {
        return { ...first, written: await save.write(first.document) };
    } catch (error) {
        if (!hadRecords || !isRefusedWrite(error)) throw error;
        console.warn('[saveFolder] Document refused; saving again with every file re-uploaded:', error);
        save.resources.forgetStoredFiles(save.folderUrl);
        const retry = await attempt();
        return { ...retry, written: await save.write(retry.document) };
    }
}

/** A 400, from `CmsClient` (`status`) or the generated Axios client (`response.status`). */
function isRefusedWrite(error: unknown): boolean {
    const e = error as { status?: unknown; response?: { status?: unknown } } | null;
    return e?.status === 400 || e?.response?.status === 400;
}

/**
 * Register the absolute URL a freshly imported folder file resolves to.
 *
 * A file imported into an open scene is uploaded eagerly, so the folder holds
 * it before the scene document names it. The bytes are also in the
 * `ResourceManager` (that is what makes the object render at once), but the
 * URL registration is what makes it still resolve after a `clearScene`, and it
 * is the same registration the loader would have made had the scene been
 * opened from storage.
 */
export function registerFolderFileUrl(
    resources: Pick<ResourceManager, 'registerAssetUrl'>,
    folderBaseUrl: string,
    uri: string,
): void {
    resources.registerAssetUrl(uri, `${folderBaseUrl}${uri}`);
}
